// src/pages/ReferralsPage.jsx
// Updated with withdrawal features

import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';
import * as signatureService from '../services/signatureService';
import '../styles/referrals-page.css';

const ReferralsPage = () => {
  const { wallet, openWalletModal } = useWallet();
  const [userCode, setUserCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek: 0
  });
  // Commission data states
  const [commissionsData, setCommissionsData] = useState({
    summary: {
      total_referrer_commissions: 0,
      total_referrals_count: 0,
      active_referrals_count: 0,
      commissions_count: 0,
      period: '30d'
    },
    commissions: [],
    referrals: {
      records: [],
      pagination: {
        total_records: 0,
        total_pages: 1,
        current_page: 1,
        page_size: 20
      }
    }
  });
  const [commissionPeriod, setCommissionPeriod] = useState('30d');
  const [commissionPage, setCommissionPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // For binding an invitation code
  const [bindCode, setBindCode] = useState('');
  const [bindingCode, setBindingCode] = useState(false);
  const [bindSuccess, setBindSuccess] = useState('');
  const [bindError, setBindError] = useState('');
  
  // Referral status
  const [referralStatus, setReferralStatus] = useState({
    isReferred: false,
    referrerCode: '',
    referrerWallet: ''
  });
  
  // Withdrawal states
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  
  // Withdrawal history states
  const [withdrawalHistory, setWithdrawalHistory] = useState({
    withdrawals: [],
    pagination: {
      total_items: 0,
      total_pages: 1,
      current_page: 1,
      page_size: 5
    }
  });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(5);
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyType, setHistoryType] = useState('referrer'); // Default to referrer commissions
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [withdrawalDetailsId, setWithdrawalDetailsId] = useState(null);
  const [withdrawalDetails, setWithdrawalDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  useEffect(() => {
    if (wallet.connected) {
      fetchReferralData();
      fetchReferrerCommissionsData();
      fetchWithdrawalHistory();
    } else {
      setLoading(false);
    }
  }, [wallet.connected, wallet.address, commissionPeriod, commissionPage, historyPage, historyStatus, historyType]);
  
  // Clean up modal classes when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);
  
  const fetchReferralData = async () => {
    if (!wallet.connected) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Check if user is referred
      try {
        const statusResponse = await api.checkReferralStatus(wallet.address);
        if (statusResponse && statusResponse.success) {
          setReferralStatus({
            isReferred: statusResponse.data.is_referred,
            referrerCode: statusResponse.data.referrer_code || '',
            referrerWallet: statusResponse.data.referrer_wallet || ''
          });
        }
      } catch (statusErr) {
        console.warn('Failed to check referral status:', statusErr);
      }
      
      // Use the correct blockchain network ID
      const networkId = 1;
      
      // Use signature caching service for valid signature
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          networkId,
          wallet.type
        );
        
        console.log('Got valid signature for referral data:', signatureData.cached ? '(cached)' : '(new)');
      } catch (sigErr) {
        console.error('Failed to get valid signature:', sigErr);
        throw new Error('Could not get valid signature. Please try again.');
      }
      
      // Get invitation code and referrals
      try {
        const response = await api.getReferrals(
          wallet.address,
          networkId,
          signatureData.signature,
          signatureData.message,
          wallet.type
        );
        
        console.log('ReferralsPage - Full API response:', response);
        
        if (response && response.success) {
          // Direct access to the data structure
          if (response.data && response.data.has_invitation_code) {
            console.log('Setting user code to:', response.data.invitation_code);
            setUserCode(response.data.invitation_code);
            setReferrals(response.data.referrals || []);
            
            // Calculate stats
            const total = response.data.total_referrals || 0;
            const now = new Date();
            const thisMonth = (response.data.referrals || []).filter(ref => {
              const date = new Date(ref.created_at);
              return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            }).length;
            
            const thisWeek = (response.data.referrals || []).filter(ref => {
              const date = new Date(ref.created_at);
              const diffTime = Math.abs(now - date);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              return diffDays <= 7;
            }).length;
            
            setReferralStats({ total, thisMonth, thisWeek });
          } else {
            // If no invitation code, try to generate one
            try {
              const codeResponse = await api.generateInvitationCode(
                wallet.address,
                networkId,
                signatureData.signature,
                signatureData.message,
                wallet.type
              );
              
              console.log('Generate code response:', codeResponse);
              
              if (codeResponse && codeResponse.success) {
                setUserCode(codeResponse.data.invitation_code);
              }
            } catch (codeErr) {
              console.error('Failed to generate invitation code:', codeErr);
              // Continue even if generation fails
            }
          }
        }
      } catch (refErr) {
        console.error('Failed to fetch referrals:', refErr);
        throw new Error('Could not retrieve referrals data. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
      setError(err.message || 'Failed to load referral data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReferrerCommissionsData = async () => {
    if (!wallet.connected) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Use signature caching service for valid signature
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          1, // Use consistent network ID
          wallet.type
        );
        
        console.log('Got valid signature for commissions data:', signatureData.cached ? '(cached)' : '(new)');
      } catch (sigErr) {
        console.error('Failed to get valid signature:', sigErr);
        throw new Error('Could not get valid signature. Please try again.');
      }
      
      // Get referrer commissions data
      const response = await api.getReferrerCommissionsData(
        wallet.address,
        signatureData.signature,
        signatureData.message,
        wallet.type,
        commissionPeriod,
        commissionPage,
        pageSize
      );
      
      if (response && response.success) {
        console.log('Referrer commissions data:', response.data);
        setCommissionsData(response.data);
      } else {
        throw new Error(response?.message || 'Failed to fetch referrer commissions data');
      }
    } catch (err) {
      console.error('Failed to fetch referrer commissions data:', err);
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
      setError(err.message || 'Failed to load referrer commissions data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchWithdrawalHistory = async () => {
    if (!wallet.connected) return;
    
    try {
      setLoadingHistory(true);
      
      // Use signature caching service for valid signature
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          1, // Use network ID = 1
          wallet.type
        );
        
        console.log('Got valid signature for withdrawal history:', signatureData.cached ? '(cached)' : '(new)');
      } catch (sigErr) {
        console.error('Failed to get valid signature:', sigErr);
        throw new Error('Could not get valid signature. Please try again.');
      }
      
      // Get withdrawal history with type filtering
      const response = await api.getUserWithdrawals(
        wallet.address,
        signatureData.signature,
        signatureData.message,
        wallet.type,
        historyPage,
        historyPageSize,
        historyStatus,
        historyType // Filter by withdrawal type
      );
      
      if (response && response.success) {
        console.log('Withdrawal history:', response.data);
        setWithdrawalHistory(response.data);
      } else {
        throw new Error(response?.message || 'Failed to fetch withdrawal history');
      }
    } catch (err) {
      console.error('Failed to fetch withdrawal history:', err);
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const fetchWithdrawalDetails = async (withdrawalId) => {
    if (!wallet.connected || !withdrawalId) return;
    
    try {
      setLoadingDetails(true);
      
      // Use signature caching service for valid signature
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          1, // Use network ID = 1
          wallet.type
        );
      } catch (sigErr) {
        console.error('Failed to get valid signature:', sigErr);
        throw new Error('Could not get valid signature. Please try again.');
      }
      
      // Get withdrawal details
      const response = await api.getWithdrawalDetails(
        withdrawalId,
        wallet.address,
        signatureData.signature,
        signatureData.message,
        wallet.type
      );
      
      if (response && response.success) {
        console.log('Withdrawal details:', response.data);
        setWithdrawalDetails(response.data);
      } else {
        throw new Error(response?.message || 'Failed to fetch withdrawal details');
      }
    } catch (err) {
      console.error('Failed to fetch withdrawal details:', err);
      setWithdrawalDetails(null);
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleBindCode = async (e) => {
    e.preventDefault();
    
    if (!wallet.connected) {
      openWalletModal();
      return;
    }
    
    if (!bindCode.trim()) {
      setBindError('Please enter a valid invitation code');
      return;
    }
    
    try {
      setBindingCode(true);
      setBindError('');
      setBindSuccess('');
      
      // Use the correct blockchain network ID
      const networkId = 1;
      
      // Use signature caching service for valid signature
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          networkId,
          wallet.type
        );
        
        console.log('Got valid signature for binding code:', signatureData.cached ? '(cached)' : '(new)');
      } catch (sigErr) {
        throw new Error('Could not get valid signature. Please try again.');
      }
      
      // Apply invitation code
      const response = await api.applyInvitationCode(
        bindCode,
        wallet.address,
        networkId,
        signatureData.signature,
        signatureData.message,
        wallet.type
      );
      
      if (response && response.success) {
        setBindSuccess('Successfully bound to invitation code!');
        setBindCode('');
        
        // Refresh data
        setTimeout(() => {
          fetchReferralData();
        }, 1000);
      } else {
        throw new Error(response?.message || 'Failed to bind invitation code');
      }
    } catch (err) {
      console.error('Failed to bind invitation code:', err);
      
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
      
      if (err.response && err.response.data) {
        setBindError(err.response.data.message || 'Failed to bind invitation code');
      } else {
        setBindError(err.message || 'Failed to bind invitation code. Please try again.');
      }
    } finally {
      setBindingCode(false);
    }
  };
  
  const handleWithdraw = async (e) => {
    e.preventDefault();
    
    if (!wallet.connected) {
      openWalletModal();
      return;
    }
    
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setWithdrawError('Please enter a valid amount');
      return;
    }
    
    try {
      setWithdrawing(true);
      setWithdrawError('');
      setWithdrawSuccess('');
      
      // Use signature caching service for valid signature - force new signature for withdrawal
      let signatureData;
      try {
        signatureData = await signatureService.getValidSignature(
          wallet.address,
          1, // Use network ID = 1
          wallet.type,
          true // forceNew = true, always get fresh signature for withdrawals
        );
        
        console.log('Got fresh signature for withdrawal');
      } catch (sigErr) {
        throw new Error('Could not get valid signature for withdrawal. Please try again.');
      }
      
      // Perform withdrawal using the dedicated referrer commissions endpoint
      const response = await api.withdrawReferrerCommissions(
        wallet.address,
        withdrawAmount,
        signatureData.signature,
        signatureData.message,
        wallet.type
      );
      
      if (response && response.success) {
        setWithdrawSuccess('Withdrawal initiated successfully!');
        setWithdrawAmount('');
        
        // Refresh data
        setTimeout(() => {
          fetchReferrerCommissionsData();
          fetchWithdrawalHistory(); // Also refresh withdrawal history
        }, 1000);
        
        // Close the modal
        document.getElementById('withdrawModal').classList.remove('show');
      } else {
        throw new Error(response?.message || 'Failed to initiate withdrawal');
      }
    } catch (err) {
      console.error('Failed to withdraw:', err);
      
      // Clear signature cache if user rejected signing
      if (err.message && (
        err.message.includes('User rejected') || 
        err.message.includes('rejected') ||
        err.message.includes('denied') ||
        err.message.includes('cancelled')
      )) {
        signatureService.clearSignatureCache();
      }
      
      if (err.response && err.response.data) {
        setWithdrawError(err.response.data.message || 'Failed to withdraw');
      } else {
        setWithdrawError(err.message || 'Failed to withdraw. Please try again.');
      }
    } finally {
      setWithdrawing(false);
    }
  };
  
  const handleViewDetails = (withdrawalId) => {
    setWithdrawalDetailsId(withdrawalId);
    fetchWithdrawalDetails(withdrawalId);
    document.getElementById('withdrawalDetailsModal').classList.add('show');
  };
  
  const closeWithdrawalDetails = () => {
    document.getElementById('withdrawalDetailsModal').classList.remove('show');
    setWithdrawalDetailsId(null);
    setWithdrawalDetails(null);
  };
  
  const copyToClipboard = (text) => {
    // Generate the full join link with the invitation code
    const fullJoinLink = `https://rwa.aeronyx.network/invitation?code=${text}`;
    
    navigator.clipboard.writeText(fullJoinLink)
      .then(() => {
        setCopySuccess(true);
        alert('Full invitation link copied to clipboard!');
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
      });
  };
  
  // Format wallet address for display
  const formatWalletAddress = (address) => {
    if (!address) return '';
    return address.substring(0, 6) + '...' + address.substring(address.length - 4);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };
  
  // Handle period change
  const handlePeriodChange = (period) => {
    setCommissionPeriod(period);
    setCommissionPage(1);
  };
  
  // Handle pagination
  const handlePageChange = (page) => {
    setCommissionPage(page);
  };
  
  if (!wallet.connected) {
    return (
      <div className="referrals-page">
        <div className="container">
          <h1 className="page-title">Referral Program</h1>
          
          <div className="connect-wallet-container">
            <p>Connect your wallet to view and manage your referrals.</p>
            <button className="btn btn-primary" onClick={openWalletModal}>
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="referrals-page">
        <div className="container">
          <h1 className="page-title">Referral Program</h1>
          
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your referral data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="referrals-page">
      <div className="container">
        <h1 className="page-title">Referral Program</h1>
        
        {error && (
          <div className="error-alert">
            <p>{error}</p>
            <button className="btn btn-outline" onClick={() => {
              fetchReferralData();
              fetchReferrerCommissionsData();
              fetchWithdrawalHistory();
            }}>
              Try Again
            </button>
          </div>
        )}
        
        {/* Commission Summary Card */}
        <div className="commission-summary-card">
          <div className="commission-summary-content">
            <div className="commission-amount">
              <h3>Total Earnings</h3>
              <div className="amount">${formatCurrency(commissionsData.summary.total_referrer_commissions)}</div>
            </div>
            <div className="commission-actions">
              <div className="period-selector">
                <button 
                  className={`period-btn ${commissionPeriod === '7d' ? 'active' : ''}`}
                  onClick={() => handlePeriodChange('7d')}
                >
                  7 Days
                </button>
                <button 
                  className={`period-btn ${commissionPeriod === '30d' ? 'active' : ''}`}
                  onClick={() => handlePeriodChange('30d')}
                >
                  30 Days
                </button>
                <button 
                  className={`period-btn ${commissionPeriod === '90d' ? 'active' : ''}`}
                  onClick={() => handlePeriodChange('90d')}
                >
                  90 Days
                </button>
                <button 
                  className={`period-btn ${commissionPeriod === 'all' ? 'active' : ''}`}
                  onClick={() => handlePeriodChange('all')}
                >
                  All Time
                </button>
              </div>
              <button className="btn btn-primary withdraw-btn" onClick={() => document.getElementById('withdrawModal').classList.add('show')}>
                Withdraw Earnings
              </button>
            </div>
          </div>
          
          <div className="commission-stats">
            <div className="stat-item">
              <div className="stat-value">{commissionsData.summary.total_referrals_count}</div>
              <div className="stat-label">Total Referrals</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{commissionsData.summary.active_referrals_count}</div>
              <div className="stat-label">Active Referrals</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{commissionsData.summary.commissions_count || 0}</div>
              <div className="stat-label">Commissions</div>
            </div>
          </div>
        </div>
        
        <div className="referrals-grid">
          {/* Left column: Invitation Code and Stats */}
          <div className="referrals-column">
            <div className="referral-card">
              <h2 className="card-title">Your Invitation Code</h2>
              
              <div className="invitation-code-display">
                <div className="code">
                  {userCode ? (
                    userCode
                  ) : loading ? (
                    'Generating...'
                  ) : (
                    'No code available'
                  )}
                </div>
                {userCode && (
                  <button 
                    className={`copy-btn ${copySuccess ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(userCode)}
                    aria-label="Copy invitation code"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0017 2.4115 15.7793 2.25992 15.5338 2.15654C15.2882 2.05316 15.0244 1.99989 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {copySuccess && <span className="tooltip">Copied!</span>}
                  </button>
                )}
              </div>
              
              <p className="invitation-code-help">
                Share this code with friends to earn referral rewards! Copying will include the full invitation link.
              </p>
              
              <div className="share-links">
                <a 
                  href={`https://twitter.com/intent/tweet?text=Join%20me%20on%20Aeronyx!%20Use%20my%20invitation%20code%20${userCode}%20to%20get%20started.%20&url=https://rwa.aeronyx.network/invitation?code=${userCode}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-link twitter"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 4.01C21.045 4.84 20.167 5.505 19.25 6C18.787 5.468 18.2 5.046 17.532 4.77C16.864 4.494 16.135 4.372 15.407 4.417C14.679 4.462 13.975 4.671 13.354 5.026C12.733 5.382 12.213 5.874 11.837 6.462C11.1554 7.56437 11.0197 8.89908 11.467 10.113C9.67026 10.0218 7.9142 9.52844 6.32582 8.66443C4.73744 7.80042 3.35219 6.58642 2.27 5.108C1.87565 5.80612 1.65045 6.60208 1.651 7.413C1.65146 8.17388 1.82099 8.924 2.14626 9.60594C2.47153 10.2879 2.94393 10.8828 3.53 11.342C2.84996 11.3239 2.18459 11.1328 1.588 10.784V10.836C1.58866 11.5378 1.80362 12.2213 2.20025 12.7967C2.59688 13.372 3.15772 13.8114 3.808 14.055C3.18472 14.2312 2.52509 14.2555 1.89 14.125C2.0861 14.7268 2.46488 15.2533 2.97456 15.6394C3.48424 16.0254 4.10235 16.2533 4.743 16.294C3.61475 17.1809 2.2265 17.6681 0.8 17.673C0.5328 17.673 0.2656 17.6538 0 17.6155C1.45842 18.5676 3.15268 19.0737 4.8825 19.072C15.4 19.072 20.0125 10.264 20.0125 2.641C20.0125 2.461 20.0075 2.281 20 2.101C20.9573 1.336 21.7832 0.407983 22.445 -0.636C21.5696 -0.237 20.6373 0.0299995 19.68 0.151C20.6811 -0.437 21.4361 -1.353 21.8 -2.454C20.8624 -1.906 19.8319 -1.5116 18.758 -1.288C18.0349 -2.02923 17.0865 -2.52354 16.0583 -2.68653C15.0302 -2.84952 13.9752 -2.67155 13.0575 -2.1784C12.1398 -1.68526 11.4103 -0.90718 10.9836 0.023333C10.557 0.953845 10.4609 2.00234 10.711 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Share on Twitter
                </a>
                
                <a 
                  href={`https://t.me/share/url?url=https://rwa.aeronyx.network/invitation?code=${userCode}&text=Join%20me%20on%20Aeronyx!%20Use%20my%20invitation%20code%20${userCode}%20to%20get%20started.`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="share-link telegram"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.05 2.31c-.439-.388-1.09-.495-1.93-.326-1.013.202-17.194 6.733-18.344 7.472-.48.34-.773.601-.802.941-.04.77.513 1.094.513 1.094l4.36 1.429c.306.656 1.48 5.715 1.71 6.432.15.488.288.707.428.8.255.17.51.134.51.134.345-.17 1.984-1.921 2.517-2.436 1.651 1.109 3.223 2.201 3.836 2.74.427.373.79.579 1.123.688.433.149.83.176 1.164.106.467-.099.985-.483 1.113-1.83.134-1.387 1.446-15.704 1.472-16.496.043-.937-.293-1.342-.67-1.748zm-3.207 2.769L7.734 11.602l-.56.193.076-.561 10.816-6.57c.351-.118.86-.322.86.071-.001.297-.083.334-.083.334z"/>
                  </svg>
                  Share on Telegram
                </a>
              </div>
            </div>
            
            <div className="referral-card">
              <h2 className="card-title">Referral Statistics</h2>
              
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{referralStats.total}</div>
                  <div className="stat-label">Total Invitees</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-value">{referralStats.thisMonth}</div>
                  <div className="stat-label">This Month</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-value">{referralStats.thisWeek}</div>
                  <div className="stat-label">This Week</div>
                </div>
              </div>
            </div>
            
            {/* Withdrawal History Card - NEW */}
            <div className="referral-card withdrawal-history-card">
              <h2 className="card-title">Withdrawal History</h2>
              
              <div className="withdrawal-filter">
                <div className="filter-group">
                  <label htmlFor="statusFilter">Status</label>
                  <select 
                    id="statusFilter" 
                    value={historyStatus} 
                    onChange={(e) => setHistoryStatus(e.target.value)}
                    disabled={loadingHistory}
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                
                <button 
                  className="btn btn-outline" 
                  onClick={() => fetchWithdrawalHistory()}
                  disabled={loadingHistory}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 12C2 16.4183 5.58172 20 10 20C14.4183 20 18 16.4183 18 12C18 7.58172 14.4183 4 10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 5V12M18 12H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Refresh
                </button>
              </div>
              
              {loadingHistory ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading withdrawal history...</p>
                </div>
              ) : withdrawalHistory.withdrawals && withdrawalHistory.withdrawals.length > 0 ? (
                <>
                  <div className="withdrawals-list-header">
                    <div className="header-id">ID</div>
                    <div className="header-amount">Amount</div>
                    <div className="header-date">Date</div>
                    <div className="header-status">Status</div>
                    <div className="header-actions">Actions</div>
                  </div>
                  
                  <div className="withdrawals-list">
                    {withdrawalHistory.withdrawals.map((withdrawal) => (
                      <div className="withdrawal-item" key={withdrawal.withdrawal_id}>
                        <div className="withdrawal-id">{withdrawal.withdrawal_id}</div>
                        <div className="withdrawal-amount">${formatCurrency(withdrawal.amount)}</div>
                        <div className="withdrawal-date">{formatDate(withdrawal.requested_at)}</div>
                        <div className={`withdrawal-status status-${withdrawal.status}`}>
                          {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                        </div>
                        <div className="withdrawal-actions">
                          <button 
                            className="details-btn"
                            onClick={() => handleViewDetails(withdrawal.withdrawal_id)}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {withdrawalHistory.pagination && withdrawalHistory.pagination.total_pages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn prev"
                        disabled={!withdrawalHistory.pagination.has_previous}
                        onClick={() => setHistoryPage(historyPage - 1)}
                      >
                        &larr; Prev
                      </button>
                      
                      <div className="pagination-info">
                        Page {withdrawalHistory.pagination.current_page} of {withdrawalHistory.pagination.total_pages}
                      </div>
                      
                      <button 
                        className="pagination-btn next"
                        disabled={!withdrawalHistory.pagination.has_next}
                        onClick={() => setHistoryPage(historyPage + 1)}
                      >
                        Next &rarr;
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-data">
                  <p>No withdrawal records found.</p>
                </div>
              )}
            </div>
            
            {!referralStatus.isReferred && (
              <div className="referral-card">
                <h2 className="card-title">Bind Invitation Code</h2>
                
                <form onSubmit={handleBindCode}>
                  <div className="form-group">
                    <label htmlFor="invitationCode">Enter Invitation Code</label>
                    <div className="input-group">
                      <input
                        type="text"
                        id="invitationCode"
                        placeholder="ALD-XXXXX"
                        value={bindCode}
                        onChange={(e) => setBindCode(e.target.value)}
                        disabled={bindingCode}
                      />
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={bindingCode || !bindCode.trim()}
                      >
                        {bindingCode ? 'Binding...' : 'Bind Code'}
                      </button>
                    </div>
                    
                    {bindError && <div className="error-message">{bindError}</div>}
                    {bindSuccess && <div className="success-message">{bindSuccess}</div>}
                  </div>
                  
                  <p className="help-text">
                    Note: Once you bind an invitation code, it cannot be changed.
                  </p>
                </form>
              </div>
            )}
            
            {referralStatus.isReferred && (
              <div className="referral-card">
                <h2 className="card-title">Your Referrer</h2>
                
                <div className="referrer-info">
                  <div className="info-item">
                    <span className="info-label">Invitation Code:</span>
                    <span className="info-value">{referralStatus.referrerCode}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="info-label">Wallet Address:</span>
                    <span className="info-value">{formatWalletAddress(referralStatus.referrerWallet)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Right column: Commission History & Referrals */}
          <div className="referrals-column">
            {/* Commission History */}
            <div className="referral-card commission-history-card">
              <h2 className="card-title">Commission History</h2>
              
              {commissionsData.commissions.length === 0 ? (
                <div className="no-data">
                  <p>No commission records found for the selected period.</p>
                </div>
              ) : (
                <>
                  <div className="commissions-list-header">
                    <div className="header-amount">Amount</div>
                    <div className="header-referral">Referral</div>
                    <div className="header-date">Date</div>
                    <div className="header-status">Status</div>
                  </div>
                  
                  <div className="commissions-list">
                    {commissionsData.commissions.map((commission, index) => (
                      <div className="commission-item" key={index}>
                        <div className="commission-amount">${formatCurrency(commission.amount)}</div>
                        <div className="commission-referral">
                          {commission.referral_info ? 
                            formatWalletAddress(commission.referral_info.wallet_address) : 
                            'Unknown'}
                        </div>
                        <div className="commission-date">{formatDate(commission.created_at)}</div>
                        <div className={`commission-status status-${commission.status}`}>
                          {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Referrals List */}
            <div className="referral-card referrals-list-card">
              <h2 className="card-title">Your Invitees</h2>
              
              {commissionsData.referrals.records.length === 0 ? (
                <div className="no-referrals">
                  <p>You haven't referred anyone yet.</p>
                  <p>Share your invitation code to start earning rewards!</p>
                </div>
              ) : (
                <>
                  <div className="referrals-list-header">
                    <div className="header-wallet">Wallet Address</div>
                    <div className="header-status">Status</div>
                    <div className="header-earnings">Earnings</div>
                    <div className="header-date">Joined</div>
                  </div>
                  
                  <div className="referrals-list">
                    {commissionsData.referrals.records.map((referral, index) => (
                      <div className="referral-item" key={index}>
                        <div className="referral-wallet">
                          {formatWalletAddress(referral.wallet_address)}
                        </div>
                        <div className={`referral-status ${referral.has_active_node ? 'active' : 'inactive'}`}>
                          {referral.has_active_node ? 'Active' : 'Inactive'}
                        </div>
                        <div className="referral-earnings">
                          ${formatCurrency(referral.commissions_generated)}
                        </div>
                        <div className="referral-date">
                          {formatDate(referral.referred_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {commissionsData.referrals.pagination.total_pages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn prev"
                        disabled={!commissionsData.referrals.pagination.has_previous}
                        onClick={() => handlePageChange(commissionPage - 1)}
                      >
                        &larr; Prev
                      </button>
                      
                      <div className="pagination-info">
                        Page {commissionPage} of {commissionsData.referrals.pagination.total_pages}
                      </div>
                      
                      <button 
                        className="pagination-btn next"
                        disabled={!commissionsData.referrals.pagination.has_next}
                        onClick={() => handlePageChange(commissionPage + 1)}
                      >
                        Next &rarr;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Withdrawal Modal */}
      <div id="withdrawModal" className="modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Withdraw Earnings</h2>
            <button 
              className="close-btn" 
              onClick={() => document.getElementById('withdrawModal').classList.remove('show')}
            >
              &times;
            </button>
          </div>
          
          <div className="modal-body">
            <div className="available-balance">
              <div className="balance-label">Available Balance</div>
              <div className="balance-amount">${formatCurrency(commissionsData.summary.total_referrer_commissions)}</div>
            </div>
            
            <form onSubmit={handleWithdraw} className="withdrawal-form">
              <div className="form-group">
                <label htmlFor="withdrawAmount">Withdrawal Amount</label>
                <div className="input-group">
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    id="withdrawAmount"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    min="0.01"
                    max={commissionsData.summary.total_referrer_commissions}
                    step="0.01"
                    disabled={withdrawing}
                  />
                  <button 
                    type="button" 
                    className="max-btn"
                    onClick={() => setWithdrawAmount(commissionsData.summary.total_referrer_commissions.toString())}
                  >
                    MAX
                  </button>
                </div>
                
                {withdrawError && <div className="error-message">{withdrawError}</div>}
                {withdrawSuccess && <div className="success-message">{withdrawSuccess}</div>}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-outline" 
                  onClick={() => document.getElementById('withdrawModal').classList.remove('show')}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > commissionsData.summary.total_referrer_commissions}
                >
                  {withdrawing ? 'Processing...' : 'Withdraw'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Withdrawal Details Modal - NEW */}
      <div id="withdrawalDetailsModal" className="modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Withdrawal Details</h2>
            <button 
              className="close-btn" 
              onClick={closeWithdrawalDetails}
            >
              &times;
            </button>
          </div>
          
          <div className="modal-body">
            {loadingDetails ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading details...</p>
              </div>
            ) : withdrawalDetails ? (
              <div className="withdrawal-details">
                <div className="details-section">
                  <h3>Transaction Information</h3>
                  <div className="detail-row">
                    <div className="detail-label">Withdrawal ID:</div>
                    <div className="detail-value">{withdrawalDetails.withdrawal_id}</div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-label">Status:</div>
                    <div className={`detail-value status-${withdrawalDetails.status}`}>
                      {withdrawalDetails.status.charAt(0).toUpperCase() + withdrawalDetails.status.slice(1)}
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-label">Amount:</div>
                    <div className="detail-value">${formatCurrency(withdrawalDetails.amount)}</div>
                  </div>
                  {withdrawalDetails.fee_amount > 0 && (
                    <>
                      <div className="detail-row">
                        <div className="detail-label">Fee:</div>
                        <div className="detail-value">${formatCurrency(withdrawalDetails.fee_amount)}</div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-label">Net Amount:</div>
                        <div className="detail-value">${formatCurrency(withdrawalDetails.net_amount)}</div>
                      </div>
                    </>
                  )}
                  <div className="detail-row">
                    <div className="detail-label">Requested:</div>
                    <div className="detail-value">{formatDate(withdrawalDetails.requested_at)}</div>
                  </div>
                  {withdrawalDetails.processed_at && (
                    <div className="detail-row">
                      <div className="detail-label">Processed:</div>
                      <div className="detail-value">{formatDate(withdrawalDetails.processed_at)}</div>
                    </div>
                  )}
                  {withdrawalDetails.transaction_hash && (
                    <div className="detail-row">
                      <div className="detail-label">Transaction Hash:</div>
                      <div className="detail-value hash-value">
                        {withdrawalDetails.transaction_hash}
                        <button
                          className="copy-hash-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(withdrawalDetails.transaction_hash);
                            alert('Transaction hash copied to clipboard!');
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0017 2.4115 15.7793 2.25992 15.5338 2.15654C15.2882 2.05316 15.0244 1.99989 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  {withdrawalDetails.rejection_reason && (
                    <div className="detail-row">
                      <div className="detail-label">Rejection Reason:</div>
                      <div className="detail-value rejection-reason">{withdrawalDetails.rejection_reason}</div>
                    </div>
                  )}
                </div>
                
                {withdrawalDetails.logs && withdrawalDetails.logs.length > 0 && (
                  <div className="details-section">
                    <h3>Activity Log</h3>
                    <div className="logs-list">
                      {withdrawalDetails.logs.map((log, index) => (
                        <div className="log-item" key={index}>
                          <div className="log-timestamp">{formatDate(log.timestamp)}</div>
                          <div className="log-action">{log.action}</div>
                          <div className="log-description">{log.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="error-message">
                <p>Could not load withdrawal details. Please try again later.</p>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={closeWithdrawalDetails}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralsPage;
